// @flow

import type { PropsSchema, EasyAction, BasicProps } from '../../type'
import type { Rules } from 'frame/core/formValidate'
import * as React from 'react'
import computeData from '../hoc/computeData'
import { validate } from 'frame/core/formValidate'
import css from './Form.scss'
import { isInIOS } from 'frame/core/browserEnv'

type Props = BasicProps & {
  style: { [string]: string },
  optionType: 'static' | 'dynamic',
  options: Array<{ text: string, value: string }>, // 静态 options
  optionText: ?string, // 动态 option text key
  optionValue: ?string, // 动态 option value key
  field: string,
  rules: Rules,
  defaultText: string,
  formValues: { [string]: string },
  blurReset: boolean,
  setFormValues: ({ [key: string]: any }) => void
};

type State = {
  isFocus: boolean,
  dynamicOptions: [] | [{ [string]: string }]
};

@computeData()
export default class InputText extends React.PureComponent<Props, State> {
  static defaultProps = {
    options: []
  };

  static schema: PropsSchema = [
    {
      key: 'field',
      title: '字段',
      type: 'text'
    },
    {
      key: 'defaultText',
      title: '默认文案',
      type: 'text'
    },
    {
      key: 'optionType',
      title: '选项类型',
      type: 'select',
      options: [['static', '静态配置'], ['dynamic', '动态 API 获取']],
      defaultValue: 'static'
    },
    {
      key: 'optionText',
      title: '选项 Text 字段',
      type: 'text',
      visible: ({ optionType }) => optionType === 'dynamic'
    },
    {
      key: 'optionValue',
      title: '选项 Value 字段',
      type: 'text',
      visible: ({ optionType }) => optionType === 'dynamic'
    },
    {
      title: '选项配置',
      type: 'array',
      key: 'options',
      visible: ({ optionType }) => optionType === 'static',
      item: [
        {
          type: 'text',
          key: 'text',
          title: '显示文字'
        },
        {
          type: 'text',
          key: 'value',
          title: '值'
        }
      ]
    },
    {
      title: '校验规则列表',
      type: 'array',
      key: 'rules',
      item: [
        {
          type: 'select',
          key: 'type',
          title: '规则：',
          options: [
            ['required', '必填'],
            ['mobile', '手机号'],
            ['idCard', '身份证'],
            ['zh_cn', '汉字'],
            ['number', '数字'],
            ['en_us', '字母']
          ],
          defaultValue: 'required'
        },
        {
          type: 'text',
          key: 'message',
          title: '提示消息'
        }
      ]
    },
    'separator',
    {
      type: 'style'
    },
    'separator',
    {
      type: 'boolean',
      key: 'blurReset',
      title: '失去焦点复位(仅ios)'
    },
    'separator',
    {
      type: 'event',
      title: '改变',
      event: 'onChange'
    },
    {
      type: 'event',
      title: '用户操作改变',
      event: 'userOperationChange'
    },
    {
      type: 'event',
      title: '校验成功',
      event: 'resolve'
    },
    {
      type: 'event',
      title: '校验失败',
      event: 'reject'
    }
  ];

  static actions: EasyAction[] = [
    {
      title: '绑定选项',
      name: 'bindOptions'
    },
    {
      title: '数据校验',
      name: 'validate'
    }
  ];

  state = {
    isFocus: false,
    dynamicOptions: []
  };

  componentDidMount () {
    let { callActions } = this.props
    const fieldValue = this.getFieldValue()
    if (fieldValue) {
      callActions('onChange', fieldValue)
    }
  }

  componentDidUpdate (preProps: Props, preState: State) {
    let { field, setFormValues, callActions } = this.props
    const fieldValue = this.getFieldValue()

    if (
      preState.dynamicOptions !== this.state.dynamicOptions &&
      this.refs.select.value !== fieldValue
    ) {
      // 更新 option 导致表单数据在下拉框中不存在
      setFormValues({ [field]: this.refs.select.value })
      return
    }

    if (this.getFieldValue(preProps) !== fieldValue) {
      // 表单数据更新
      callActions('onChange', fieldValue)
    }
  }

  getFieldValue = (props?: Props): string => {
    const { field, formValues } = props || this.props
    return formValues[field] == null ? '' : formValues[field]
  };

  actionBindOptions = (data: void, dynamicOptions: [any]) => {
    this.setState({
      dynamicOptions
    })
  };

  actionValidate = () => {
    let { rules, callActions } = this.props
    const fieldValue = this.getFieldValue()
    if (rules && rules.length > 0) {
      let error = validate(fieldValue, rules)
      if (!error.success) {
        callActions('reject', error)
      } else {
        callActions('resolve')
      }
    } else {
      callActions('resolve')
    }
  };

  onChange = (event: SyntheticEvent<HTMLSelectElement>) => {
    let { field, setFormValues, callActions } = this.props
    const value: string = event.currentTarget.value
    setFormValues({ [field]: value })
    callActions('userOperationChange', value)
  };

  handelBlur = (event: SyntheticEvent<HTMLInputElement>) => {
    const { blurReset } = this.props
    if (blurReset && isInIOS() && document.body) {
      setTimeout(() => {
        document.body && (document.body.scrollTop += 1)
      }, 1)
    }
  };

  render () {
    if (!this.props.visible) {
      return null
    }
    const {
      id,
      style,
      optionType,
      options: staticOption,
      optionText,
      optionValue,
      defaultText
    } = this.props
    const { dynamicOptions = [] } = this.state

    const options: Array<{ value: string, text: string }> =
      optionType === 'static'
        ? staticOption
        : dynamicOptions instanceof Array
        ? dynamicOptions.map(option => ({
          value: option[optionValue || ''],
          text: option[optionText || '设置错误']
        }))
        : []

    return (
      <select
        type='text'
        onChange={this.onChange}
        value={this.getFieldValue()}
        style={style}
        id={id}
        onBlur={this.handelBlur}
        className={css.select}
        ref='select'
      >
        {defaultText && <option value={''}>{defaultText}</option>}
        {options.map((option, i) => (
          <option key={i} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
    )
  }
}



// WEBPACK FOOTER //
// ./src/projects/easy/public/Comps/form/FormSelect.js